import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Tabs, Divider } from 'antd';
import { UserOutlined, MedicineBoxOutlined, ShopOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import sellerService from '../services/sellerService';
import branchManagerService, { BranchManagerResponse } from '../services/branchManagerService';

const { Title, Text } = Typography;

interface LoginForm {
  email: string;
  password: string;
}

interface BranchManagerLoginForm {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  // const [activeTab, setActiveTab] = useState('seller'); // Commented out seller login
  const [activeTab, setActiveTab] = useState('branch'); // Set to 'branch' by default
  const navigate = useNavigate();

  // Commented out seller login handler
  /*
  const handleLogin = async (values: LoginForm) => {
    setLoading(true);
    try {
      console.log('Attempting login with:', { email: values.email, password: '[REDACTED]' });
      
      // Call the seller login service (password not sent to server for now)
      const response = await sellerService.loginSeller({ email: values.email });
      
      console.log('Login response:', response);
      
      if (response.success) {
        // Store token and user info
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          console.log('Login successful:', response.data.user);
          
          // Navigate to main seller dashboard
          console.log('Seller login, redirecting to main dashboard...');
          message.success(`Login successful! Welcome, ${response.data.user.seller_name}!`);
          navigate('/main');
        } else {
          message.error('Login failed - no token received');
        }
      } else {
        message.error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Login failed';
        message.error(errorMessage);
      } else if (error.request) {
        // Network error
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Other error
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  */

  const handleBranchManagerLogin = async (values: BranchManagerLoginForm) => {
    setLoading(true);
    try {
      console.log('Attempting branch manager login with:', { email: values.email, password: '[REDACTED]' });
      
      // Call the branch manager login service (password not sent to server for now)
      const response = await branchManagerService.loginBranchManager({
        email: values.email
      }) as BranchManagerResponse;
      
      console.log('Branch manager login response:', response);
      
      if (response.success) {
        // Store token and user info
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify({
            id: response.data.user.id,
            username: response.data.user.username,
            email: response.data.user.email,
            manager_name: response.data.user.manager_name,
            branch_id: response.data.user.branch_id,
            is_branch_manager: true
          }));
          
          console.log('Branch manager login successful:', response.data.user);
          
          // Navigate to branch dashboard
          console.log('Branch manager login, redirecting to branch dashboard...');
          message.success(`Login successful! Welcome, ${response.data.user.manager_name}!`);
          navigate('/branch');
        } else {
          message.error('Login failed - no token received');
        }
      } else {
        message.error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Branch manager login error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Login failed';
        message.error(errorMessage);
      } else if (error.request) {
        // Network error
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Other error
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 450,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <MedicineBoxOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Medicine CRM
          </Title>
          <Text type="secondary">
            Choose your login type
          </Text>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          items={[
            // Commented out seller login tab - only branch login is enabled
            /*
            {
              key: 'seller',
              label: (
                <span>
                  <MedicineBoxOutlined />
                  Seller Login
                </span>
              ),
              children: (
                <Form
                  name="sellerLogin"
                  onFinish={handleLogin}
                  layout="vertical"
                  size="large"
                  onFinishFailed={(errorInfo) => {
                    console.log('Form validation failed:', errorInfo);
                  }}
                >
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter your email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Enter your email address"
                      type="email"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      { required: true, message: 'Please enter your password' },
                      { min: 6, message: 'Password must be at least 6 characters' }
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter your password"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: '100%', height: 48 }}
                    >
                      Login as Seller
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            */
            {
              key: 'branch',
              label: (
                <span>
                  <ShopOutlined />
                  Branch Manager Login
                </span>
              ),
              children: (
                <Form
                  name="branchManagerLogin"
                  onFinish={handleBranchManagerLogin}
                  layout="vertical"
                  size="large"
                  onFinishFailed={(errorInfo) => {
                    console.log('Form validation failed:', errorInfo);
                  }}
                >
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter your email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Enter your email address"
                      type="email"
                      autoComplete="off"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      { required: true, message: 'Please enter your password' },
                      { min: 6, message: 'Password must be at least 6 characters' }
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter your password"
                      autoComplete="new-password"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: '100%', height: 48 }}
                    >
                      Login as Branch Manager
                    </Button>
                  </Form.Item>
                </Form>
              )
            }
          ]}
        />

        <Divider />

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Typography.Text type="secondary">
            Don't have an account?{' '}
            <Typography.Link onClick={() => navigate('/register')}>
              Register here
            </Typography.Link>
          </Typography.Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;
